import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography, CircularProgress,
  FormControlLabel, Switch, Chip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { TEXT, SPACE } from '../../shared_components';
import { checkApiStatus } from '../../../../utils_v2';
import { globalGetService, globalPostService } from '../../../../utils_v2/globalApiServices';
import { getProgramPlanDetailApi, getStageTemplateStagesApi, getStagePreviewApi, changePlanStageApi } from '../apiService';
import ConfirmActionDialog from './ConfirmActionDialog';
import { trackActivity } from '../../../../utils/mixpanel';

const errorMessage = (response) => (response && response.data && response.data.message) || 'Unable to process the request due to a technical error.';

const guardLabel = (guard) => (typeof guard === 'string' ? guard : guard?.name || guard?.label || guard?.description || guard?.message || JSON.stringify(guard));

const OptionField = ({ label, options, value, onChange, disabled = false, required = true }) => (
  <Autocomplete
    options={options}
    disabled={disabled}
    getOptionLabel={(option) => option?.name || option?.label || ''}
    getOptionSelected={(option, current) => option.id === current.id}
    value={value || null}
    onChange={(event, newValue) => onChange(newValue || null)}
    renderInput={(params) => (
      <TextField {...params} required={required} label={label} variant="outlined" size="small" InputLabelProps={{ shrink: true }} />
    )}
  />
);

/* `plan` here is the list row, which doesn't carry `stage_template`/full
   payload fields — those only come back from the plan detail endpoint, so
   this dialog re-fetches the plan on open to scope the target-stage options.
   Runtime stage changes go through preview → (confirm) → change-stage, kept
   separate from the Stage Config admin transition-configuration API. */
const ChangePlanStage = ({ plan, canOverride, onClose, onSaved, enqueueSnackbar }) => {
  const [planDetail, setPlanDetail] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [data, setData] = useState({
    selectedStage: null,
    note: '',
    overrideMode: false,
    overrideReason: '',
  });

  const updateData = (patch) => setData((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    globalGetService(getProgramPlanDetailApi(plan?.slug)).then((response) => {
      if (checkApiStatus(response)) {
        const detail = response?.data?.data;
        setPlanDetail(detail);
        if (detail?.stage_template?.id) {
          globalGetService(getStageTemplateStagesApi(detail?.stage_template?.id)).then((stagesResponse) => {
            setLoading(false);
            if (checkApiStatus(stagesResponse)) {
              setStages(stagesResponse?.data?.data || []);
            } else {
              enqueueSnackbar(errorMessage(stagesResponse), { variant: 'error' });
            }
          });
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.slug]);

  useEffect(() => {
    if (!data.selectedStage) { setPreview(null); return; }
    updateData({ overrideMode: false, overrideReason: '' });
    setLoadingPreview(true);
    globalGetService(getStagePreviewApi(plan?.slug), { target_stage: data.selectedStage.id }).then((response) => {
      setLoadingPreview(false);
      if (checkApiStatus(response)) {
        setPreview(response?.data?.data);
      } else {
        setPreview(null);
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.selectedStage]);

  const canExecute = data.selectedStage && (data.overrideMode ? data.overrideReason.trim() : preview?.transition_configured);

  const executeChange = () => {
    setExecuting(true);
    const payload = {
      stage_id: data.selectedStage.id,
      ...(data?.note.trim() ? { note: data.note.trim() } : {}),
      ...(preview?.requires_confirmation ? { confirmed: true } : {}),
      ...(data?.overrideMode ? { override: true, override_reason: data.overrideReason.trim() } : {}),
      // override: true,
      // override_reason: "Required reason for overriding transition restrictions"
    };
    globalPostService(changePlanStageApi(planDetail?.slug), payload).then((response) => {
      setExecuting(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar('Stage changed', { variant: 'success' });
        setConfirmOpen(false);
        onSaved();
        trackActivity('Project Management', {
          event_type: 'Change Plan Stage Success',
          page_title: 'Planner Tracker Change Plan Stage',
          plan_slug: planDetail?.slug || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: 'Change Plan Stage Failed',
          page_title: 'Planner Tracker Change Plan Stage',
          plan_slug: planDetail?.slug || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const handlePrimaryAction = () => {
    if (!canExecute) return;
    if (data.overrideMode || preview?.requires_confirmation) {
      setConfirmOpen(true);
    } else {
      executeChange();
    }
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>Change Stage</Typography>
        <Typography variant="body2" style={{ color: TEXT.secondary, marginTop: 2 }}>{plan?.name}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: SPACE.md }}>
            <CircularProgress size={24} />
          </div>
        ) : !planDetail?.stage_template?.id ? (
          <Typography variant="body2" style={{ color: TEXT.secondary }}>
            This plan has no stage template configured.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <OptionField
                label="Target Stage"
                options={(stages || []).filter((s) => s?.id !== planDetail?.current_stage?.id)}
                value={data.selectedStage}
                onChange={(value) => updateData({ selectedStage: value })}
              />
            </Grid>

            {loadingPreview ? (
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: SPACE.sm }}>
                  <CircularProgress size={20} />
                </div>
              </Grid>
            ) : preview ? (
              <Grid item xs={12}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {preview?.from_stage?.name || planDetail?.current_stage?.name} → {preview?.to_stage?.name}
                </Typography>
                {!preview?.transition_configured ? (
                  <Typography variant="body2" style={{ color: '#d29922', marginTop: 6 }}>
                    No transition is configured between these stages.
                  </Typography>
                ) : null}
                {(preview?.guards || []).length ? (
                  <div style={{ marginTop: 8 }}>
                    <Typography variant="caption" style={{ color: TEXT.secondary, fontWeight: 600 }}>Requirements</Typography>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {(preview?.guards || []).map((guard, index) => (
                        <Chip
                          key={guard?.id ?? index}
                          label={guardLabel(guard)}
                          size="small"
                          variant="outlined"
                          style={guard?.passed === false ? { color: '#d29922', borderColor: '#d29922' } : guard?.passed === true ? { color: '#3fb950', borderColor: '#3fb950' } : undefined}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={2} label="Note (optional)" variant="outlined" size="small"
                InputLabelProps={{ shrink: true }}
                value={data.note} onChange={(event) => updateData({ note: event.target.value })}
              />
            </Grid>

            {canOverride && preview && !preview?.transition_configured ? (
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={data.overrideMode}
                      onChange={(event) => updateData({ overrideMode: event.target.checked })}
                      color="primary"
                    />
                  )}
                  label="Override — force this stage change"
                />
                {data.overrideMode ? (
                  <TextField
                    fullWidth multiline minRows={2} label="Override Reason" variant="outlined" size="small" required
                    InputLabelProps={{ shrink: true }}
                    value={data.overrideReason} onChange={(event) => updateData({ overrideReason: event.target.value })}
                  />
                ) : null}
              </Grid>
            ) : null}
          </Grid>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading || loadingPreview || !canExecute}
          onClick={handlePrimaryAction}
        >
          Change Stage
        </Button>
      </DialogActions>

      {confirmOpen ? (
        <ConfirmActionDialog
          title={data.overrideMode ? 'Override Stage Change' : 'Confirm Stage Change'}
          description={data.overrideMode ? data.overrideReason : preview?.confirm_message}
          content={`Move "${plan?.name}" to ${preview?.to_stage?.name}?`}
          confirmText="Confirm"
          confirmColor={data.overrideMode ? 'secondary' : 'primary'}
          loading={executing}
          onClose={() => setConfirmOpen(false)}
          onConfirm={executeChange}
        />
      ) : null}
    </Dialog>
  );
};

export default ChangePlanStage;
